import React, { useState, useEffect } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/solid";

const ScoreBoxForDoubleAnswer = ({ practiceItemId, onIsAttempted }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(
          `/api/get-user-responses/${practiceItemId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch responses");
        }
        const responseData = await response.json();
        setResponses(responseData);
        setLoading(false);
        if (responseData.length > 0) {
          onIsAttempted(responseData[0].isAttempted);
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
        setLoading(false);
      }
    };

    if (practiceItemId) {
      fetchResponses();
    }
  }, [practiceItemId, onIsAttempted]);

  const toggleBox = () => {
    setIsOpen(!isOpen);
  };

  const deleteResponse = async (id) => {
    try {
      await fetch(`/api/delete-response/${id}`, { method: "DELETE" });
      const updatedResponses = responses.filter(
        (response) => response.id !== id
      );
      setResponses(updatedResponses);
    } catch (error) {
      console.error("Error deleting response:", error);
    }
  };

  function removePunctuation(sentence) {
    return sentence.replace(/[,.]/g, "");
  }

  return (
    <div className="p-4 m-4 border rounded-lg shadow-lg bg-white">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Your Responses</h2>
        <button
          onClick={toggleBox}
          className="flex items-center text-gray-500 focus:outline-none"
        >
          {isOpen ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {isOpen && (
        <div>
          {loading ? (
            <p>Loading...</p>
          ) : (
            responses.map((response, index) => (
              <div
                key={response.id}
                className={`mb-6 ${
                  index !== responses.length - 1
                    ? "pb-6 border-b border-gray-300"
                    : ""
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
                  <p className="font-bold mr-2">{index + 1}.</p>
                  <div className="flex-grow w-full">
                    <div className="overflow-x-auto w-full">
                      <table className="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-lg mb-4">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Date/Time
                            </th>
                            <th
                              scope="col"
                              className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Correct Options
                            </th>
                            <th
                              scope="col"
                              className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Incorrect Options
                            </th>
                            <th
                              scope="col"
                              className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Total Score
                            </th>
                            <th
                              scope="col"
                              className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Remarks
                            </th>
                            <th
                              scope="col"
                              className="relative px-3 sm:px-6 py-3"
                            ></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                              {new Date(response.updatedAt).toLocaleDateString(
                                "en-GB"
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                              <span className="px-2 py-1 rounded-lg">
                                {response.userCorrectOptionNumber}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                              <span className="px-2 py-1 rounded-lg">
                                {response.userIncorrectOptionNumber}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                              {response.totalScore}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                              {Array.isArray(response.remarks)
                                ? response.remarks.join(", ")
                                : response.remarks}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-right text-sm font-medium relative">
                              <TrashIcon
                                className="h-5 w-5 text-gray-500 cursor-pointer absolute top-2 right-2 transition duration-300 hover:text-red-600"
                                onClick={() => deleteResponse(response.id)}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="border border-gray-300 rounded-lg p-4">
                      <p className="font-bold">User Response:</p>
                      <div className="flex flex-wrap mt-2">
                        {response.userCorrectOptions
                          .split(".")
                          .map((sentence, index) => (
                            <div
                              key={`correct_${index}`}
                              className="px-1 rounded-md mr-1 mb-1 text-xs md:text-sm bg-green-300"
                            >
                              {removePunctuation(sentence.trim())}
                            </div>
                          ))}

                        {response.userIncorrectOptions.trim() !== "" &&
                          response.userIncorrectOptions
                            .split(".")
                            .map((sentence, index) => (
                              <div
                                key={`incorrect_${index}`}
                                className="px-1 rounded-md mr-1 mb-1 text-xs md:text-sm bg-red-400"
                              >
                                {removePunctuation(sentence.trim())}
                              </div>
                            ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreBoxForDoubleAnswer;
